import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Pencil,
  X,
  Calendar as CalendarIcon,
  AlertCircle,
  Sun,
  Moon,
} from "lucide-react";
import { useTheme } from "@/App";
import { cn, formatDate, formatDateShort } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAvailability } from "@/hooks/useAvailability";
import { useBookings } from "@/hooks/useBookings";

type RangeType = "booked" | "hold" | "second-hold";

type AgendaItem = {
  id: string;
  type: 'booked' | 'hold' | 'second-hold';
  start: string;
  end: string;
  project?: string;
  source: 'availability' | 'booking';
};

const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// Colors used for split-cell overlap display
const OVERLAP_COLORS = {
  booked: '#4A6CF7',
  hold: '#C084FC',
  'second-hold': '#94A3B8',
} as const;

const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year: number, month: number) => {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
};

export default function CalendarPage() {
  const { isDark, toggleTheme, currency, dateFormat } = useTheme();
  const isPencil = currency === 'GBP';
  const fh = isPencil ? 'First Pencil' : 'First Hold';
  const sh = isPencil ? 'Second Pencil' : 'Second Hold';
  const { toast } = useToast();
  const { ranges, loading: availLoading, addRange, updateRange, deleteRange } = useAvailability();
  const { bookings, loading: bookingsLoading, updateBooking, deleteBooking } = useBookings(currency);
  const isLoading = availLoading || bookingsLoading;

  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [showGoogleModal, setShowGoogleModal] = useState(false);

  // Selected day for day-detail panel (shows existing ranges for that date)
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  // Two-click range entry: first click sets pending start date
  const [clickStart, setClickStart] = useState<string | null>(null);
  // Add range modal (opened from calendar two-click flow or day-detail "Add Range" button)
  const [showAddModal, setShowAddModal] = useState(false);

  // Form State
  const [newRange, setNewRange] = useState({
    type: "booked" as RangeType,
    start: "",
    end: "",
    project: ""
  });

  // Agenda item editing
  const [editingAgendaItem, setEditingAgendaItem] = useState<AgendaItem | null>(null);
  const [agendaDraft, setAgendaDraft] = useState<{ project: string; type: RangeType; start: string; end: string }>({ project: "", type: "booked", start: "", end: "" });

  const pad2 = (n: number) => String(n).padStart(2, "0");

  const pageClasses = isDark
    ? "bg-gradient-to-b from-[#0f1419] via-[#131820] to-[#0a0e12] text-white"
    : "bg-[#E3E8F1] text-[#1A1F36]";

  const cardClasses = cn(
    "backdrop-blur-2xl rounded-[2.5rem] border shadow-xl shadow-blue-900/5",
    isDark
      ? "bg-white/10 border-white/10 [@supports_not_(backdrop-filter:blur(1px))]:bg-[#131820]"
      : "bg-white/80 border-white [@supports_not_(backdrop-filter:blur(1px))]:bg-white"
  );
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const labelClasses = cn(
    "text-sm font-bold uppercase tracking-[0.2em]",
    isDark ? "text-white/30" : "text-[#45556c]"
  );

  const inputClasses = cn(
    "w-full px-4 py-3 rounded-2xl border transition-all outline-none",
    isDark ? "bg-white/5 border-white/10 text-white placeholder-white/20 focus:border-white/30" : "bg-white/50 border-gray-200 text-[#1A1F36] placeholder-[#1A1F36]/30 focus:border-gray-400"
  );

  const handlePrevMonth = () => {
    setSelectedDay(null);
    setClickStart(null);
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(prev => prev - 1); }
    else setCurrentMonth(prev => prev - 1);
  };

  const handleNextMonth = () => {
    setSelectedDay(null);
    setClickStart(null);
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(prev => prev + 1); }
    else setCurrentMonth(prev => prev + 1);
  };

  const calendarGrid = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const cells: Array<{ day: number | null; date: string | null }> = [];
    for (let i = 0; i < firstDay; i++) cells.push({ day: null, date: null });
    for (let i = 1; i <= daysInMonth; i++) {
      cells.push({ day: i, date: `${currentYear}-${pad2(currentMonth + 1)}-${pad2(i)}` });
    }
    while (cells.length % 7 !== 0) cells.push({ day: null, date: null });
    return cells;
  }, [currentYear, currentMonth]);

  const getDayStatus = (dateStr: string | null) => {
    if (!dateStr) return null;
    const d = new Date(dateStr + "T00:00:00");
    const bookedRange = ranges.find(r => r.type === "booked" && d >= new Date(r.start + "T00:00:00") && d <= new Date(r.end + "T00:00:00"));
    if (bookedRange) return "booked";
    const booking = bookings.find(b => b.startDate && b.endDate && d >= new Date(b.startDate + "T00:00:00") && d <= new Date(b.endDate + "T00:00:00"));
    if (booking) return "booked";
    const hold = ranges.find(r => r.type === "hold" && d >= new Date(r.start + "T00:00:00") && d <= new Date(r.end + "T00:00:00"));
    if (hold) return "hold";
    const secondHold = ranges.find(r => r.type === "second-hold" && d >= new Date(r.start + "T00:00:00") && d <= new Date(r.end + "T00:00:00"));
    if (secondHold) return "second-hold";
    return null;
  };

  // Returns ALL distinct statuses on a date (enables split-cell overlap display)
  const getDayStatuses = (dateStr: string | null): string[] => {
    if (!dateStr) return [];
    const d = new Date(dateStr + 'T00:00:00');
    const statuses: string[] = [];
    for (const b of bookings) {
      if (b.startDate && b.endDate && d >= new Date(b.startDate + 'T00:00:00') && d <= new Date(b.endDate + 'T00:00:00')) {
        if (!statuses.includes('booked')) statuses.push('booked');
      }
    }
    for (const r of ranges) {
      if (r.type === 'booked' && d >= new Date(r.start + 'T00:00:00') && d <= new Date(r.end + 'T00:00:00')) {
        if (!statuses.includes('booked')) statuses.push('booked');
      }
      if (r.type === 'hold' && d >= new Date(r.start + 'T00:00:00') && d <= new Date(r.end + 'T00:00:00')) {
        if (!statuses.includes('hold')) statuses.push('hold');
      }
      if (r.type === 'second-hold' && d >= new Date(r.start + 'T00:00:00') && d <= new Date(r.end + 'T00:00:00')) {
        if (!statuses.includes('second-hold')) statuses.push('second-hold');
      }
    }
    return statuses;
  };

  // Returns array of company names for all bookings overlapping a date (for dot indicator)
  const getDayBookings = (dateStr: string | null): string[] => {
    if (!dateStr) return [];
    const d = new Date(dateStr + 'T00:00:00');
    return bookings
      .filter(b => b.startDate && b.endDate &&
        d >= new Date(b.startDate + 'T00:00:00') &&
        d <= new Date(b.endDate + 'T00:00:00'))
      .map(b => b.company || 'Booked');
  };

  // Returns all ranges + bookings overlapping a date (for day-detail panel)
  const getDayRanges = (dateStr: string): AgendaItem[] => {
    const d = new Date(dateStr + 'T00:00:00');
    const items: AgendaItem[] = [];
    for (const r of ranges) {
      if (d >= new Date(r.start + 'T00:00:00') && d <= new Date(r.end + 'T00:00:00')) {
        items.push({ id: r.id, type: r.type, start: r.start, end: r.end, project: r.project, source: 'availability' });
      }
    }
    for (const b of bookings) {
      if (b.startDate && b.endDate && d >= new Date(b.startDate + 'T00:00:00') && d <= new Date(b.endDate + 'T00:00:00')) {
        items.push({ id: b.id, type: 'booked', start: b.startDate, end: b.endDate, project: b.company || b.role || undefined, source: 'booking' });
      }
    }
    return items;
  };

  // Only connects run bars when the adjacent cell is also single-status with the same type
  const getRunPosition = (dateStr: string | null, status: string | null) => {
    if (!dateStr || !status) return "single";
    const d = new Date(dateStr + "T00:00:00");
    const prevD = new Date(d); prevD.setDate(d.getDate() - 1);
    const nextD = new Date(d); nextD.setDate(d.getDate() + 1);
    const fmt = (dt: Date) => `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
    const isEndOfRow = d.getDay() === 0;
    const isStartOfRow = d.getDay() === 1;
    const prevStatuses = getDayStatuses(fmt(prevD));
    const nextStatuses = getDayStatuses(fmt(nextD));
    const hasPrev = !isStartOfRow && prevStatuses.length === 1 && prevStatuses[0] === status;
    const hasNext = !isEndOfRow && nextStatuses.length === 1 && nextStatuses[0] === status;
    if (hasPrev && hasNext) return "middle";
    if (hasPrev) return "end";
    if (hasNext) return "start";
    return "single";
  };

  const handleAddRange = async () => {
    if (!newRange.start || !newRange.end) {
      toast({ title: "Error", description: "Please select start and end dates.", variant: "destructive" });
      return;
    }
    if (new Date(newRange.start) > new Date(newRange.end)) {
      toast({ title: "Error", description: "End date must be after start date.", variant: "destructive" });
      return;
    }
    await addRange({
      type: newRange.type,
      start: newRange.start,
      end: newRange.end,
      project: newRange.project || undefined,
    });
    setNewRange({ type: "booked", start: "", end: "", project: "" });
    setShowAddModal(false);
    toast({ title: "Success", description: "Range saved." });
  };

  const handleDeleteRange = async (id: string) => {
    await deleteRange(id);
    toast({ title: "Deleted", description: "Range removed." });
  };

  const openAgendaEdit = (item: AgendaItem) => {
    setEditingAgendaItem(item);
    setAgendaDraft({ project: item.project || "", type: item.type, start: item.start, end: item.end });
  };

  const handleSaveAgendaItem = async () => {
    if (!editingAgendaItem) return;
    if (editingAgendaItem.source === 'availability') {
      await updateRange(editingAgendaItem.id, { type: agendaDraft.type, start: agendaDraft.start, end: agendaDraft.end, project: agendaDraft.project || undefined });
    } else {
      const existing = bookings.find(b => b.id === editingAgendaItem.id);
      if (existing) {
        if (agendaDraft.type !== 'booked') {
          // Changing a booking to a hold: delete the booking and add an availability range instead
          await deleteBooking(editingAgendaItem.id);
          await addRange({ type: agendaDraft.type, start: agendaDraft.start, end: agendaDraft.end, project: agendaDraft.project || existing.company || undefined });
        } else {
          let newDays = existing.days;
          if (agendaDraft.start && agendaDraft.end) {
            try {
              const s = new Date(agendaDraft.start + "T00:00:00");
              const e = new Date(agendaDraft.end + "T00:00:00");
              if (e >= s) {
                let count = 0;
                const d = new Date(s);
                while (d <= e) { if (d.getDay() !== 0 && d.getDay() !== 6) count++; d.setDate(d.getDate() + 1); }
                newDays = count;
              }
            } catch { /* keep existing.days */ }
          }
          await updateBooking(editingAgendaItem.id, {
            company: agendaDraft.project || existing.company,
            role: existing.role,
            startDate: agendaDraft.start,
            endDate: agendaDraft.end,
            days: newDays,
            rate: existing.rate,
            rateType: existing.rateType,
            hoursPerDay: existing.hoursPerDay,
            status: existing.status,
            notes: existing.notes,
          }, existing.earnings);
        }
      }
    }
    setEditingAgendaItem(null);
    toast({ title: "Saved", description: "Changes saved." });
  };

  const handleDeleteItem = async (item: AgendaItem) => {
    const label = item.project || (item.type === "hold" ? fh : item.type === "second-hold" ? sh : "Booking");
    const confirmed = window.confirm(`Delete "${label}"? This cannot be undone.`);
    if (!confirmed) return;
    if (item.source === 'booking') {
      await deleteBooking(item.id);
    } else {
      await deleteRange(item.id);
    }
    toast({ title: "Deleted", description: `${label} removed.` });
  };

  const earliestAvailable = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const intervals: Array<{ start: Date; end: Date }> = [];
    for (const r of ranges) {
      // Only hard-booked ranges block availability; holds do not
      if (r.type === 'booked') {
        intervals.push({ start: new Date(r.start + 'T00:00:00'), end: new Date(r.end + 'T00:00:00') });
      }
    }
    for (const b of bookings) {
      if (b.startDate && b.endDate) {
        intervals.push({ start: new Date(b.startDate + 'T00:00:00'), end: new Date(b.endDate + 'T00:00:00') });
      }
    }

    // Walk forward from today until we land on a free day
    const cursor = new Date(today);
    let safety = 0;
    while (safety++ < 730) {
      const blocked = intervals.some(i => cursor >= i.start && cursor <= i.end);
      if (!blocked) break;
      const latestEnd = intervals
        .filter(i => cursor >= i.start && cursor <= i.end)
        .reduce((m, i) => (i.end > m ? i.end : m), new Date(0));
      cursor.setTime(latestEnd.getTime() + 86400000);
    }
    if (safety > 730) return null;

    const windowStart = new Date(cursor);

    // Find the next interval that starts after this free window opens
    const futureMins = intervals
      .map(i => i.start)
      .filter(s => s > windowStart);
    const windowEnd = futureMins.length > 0
      ? new Date(Math.min(...futureMins.map(s => s.getTime())) - 86400000)
      : null;

    const days = windowEnd
      ? Math.round((windowEnd.getTime() - windowStart.getTime()) / 86400000) + 1
      : null;

    return { start: windowStart, end: windowEnd, days };
  }, [ranges, bookings]);

  const filteredItems = useMemo((): AgendaItem[] => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const overlaps = (start: string, end: string) => {
      const s = new Date(start + "T00:00:00");
      const e = new Date(end + "T00:00:00");
      return s <= lastDay && e >= firstDay;
    };
    const availItems: AgendaItem[] = ranges
      .filter(r => overlaps(r.start, r.end))
      .map(r => ({ id: r.id, type: r.type, start: r.start, end: r.end, project: r.project, source: 'availability' as const }));
    const bookingItems: AgendaItem[] = bookings
      .filter(b => b.startDate && b.endDate && overlaps(b.startDate, b.endDate))
      .map(b => ({ id: b.id, type: 'booked' as const, start: b.startDate, end: b.endDate, project: b.company || b.role || undefined, source: 'booking' as const }));
    return [...availItems, ...bookingItems].sort((a, b) => a.start.localeCompare(b.start));
  }, [ranges, bookings, currentMonth, currentYear]);

  // Shared Add Range form rendered in both the sidebar panel and the modal
  const addRangeForm = (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label className={labelClasses}>Type</Label>
        <Select value={newRange.type} onValueChange={(v: RangeType) => setNewRange({...newRange, type: v})}>
          <SelectTrigger className={cn("rounded-2xl h-12 border font-bold px-4", isDark ? "bg-white/5 border-white/10 text-white" : "bg-white/50 border-gray-200 text-[#1A1F36]")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={cn("rounded-2xl border", isDark ? "bg-[#1A1F36] border-white/10 text-white" : "bg-white border-gray-200 text-[#1A1F36]")}>
            <SelectItem value="booked">Booked</SelectItem>
            <SelectItem value="hold">{fh}</SelectItem>
            <SelectItem value="second-hold">{sh}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className={labelClasses}>Start Date</Label>
            <input
              type="date"
              className={inputClasses}
              value={newRange.start}
              onChange={e => setNewRange({...newRange, start: e.target.value})}
            />
          </div>
          <div>
            <Label className={labelClasses}>End Date</Label>
            <input
              type="date"
              className={inputClasses}
              value={newRange.end}
              min={newRange.start || undefined}
              onFocus={e => {
                if (!e.target.value && newRange.start) {
                  e.target.value = newRange.start;
                  setTimeout(() => { if (!newRange.end) e.target.value = ''; }, 0);
                }
              }}
              onChange={e => setNewRange({...newRange, end: e.target.value})}
            />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label className={labelClasses}>Client Name</Label>
        <input
          placeholder="e.g. Studio XYZ"
          value={newRange.project}
          onChange={e => setNewRange({...newRange, project: e.target.value})}
          className={inputClasses}
        />
      </div>

      <button
        onClick={handleAddRange}
        className="w-full bg-black text-white py-4 rounded-full font-bold uppercase tracking-[0.2em] shadow-lg shadow-black/20 hover:opacity-80 transition-opacity active:scale-[0.98]"
      >
        Save Range
      </button>
    </div>
  );

  // Helper to handle clicks on empty / today (no booking) cells
  const handleEmptyDayClick = (dateStr: string) => {
    if (clickStart) {
      const start = clickStart <= dateStr ? clickStart : dateStr;
      const end = clickStart <= dateStr ? dateStr : clickStart;
      setNewRange(prev => ({ ...prev, start, end }));
      setClickStart(null);
      setShowAddModal(true);
    } else {
      setClickStart(dateStr);
      setNewRange(prev => ({ ...prev, start: dateStr, end: '' }));
    }
  };

  // Helper to handle clicks on occupied cells
  const handleOccupiedDayClick = (dateStr: string) => {
    if (selectedDay === dateStr) { setSelectedDay(null); return; }
    setClickStart(null);
    setSelectedDay(dateStr);
  };

  return (
    <div className={cn("min-h-screen font-sans selection:bg-blue-200 relative overflow-hidden", pageClasses)}>
      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className={`absolute -top-40 -right-40 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl ${prefersReducedMotion ? '' : 'animate-pulse'}`} />
          <div className={`absolute -bottom-40 -left-40 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl ${prefersReducedMotion ? '' : 'animate-pulse'}`} style={{ animationDelay: "1s" }} />
        </div>
      )}
      <main className="max-w-[1400px] mx-auto space-y-8 relative z-10 p-4 md:p-8 pt-4 pb-28 md:pb-8">
        <header className="flex justify-between items-start gap-4 md:gap-8 mb-8">
          <div className="flex-1">
            <h1 className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-black tracking-tight mb-2">Calendar</h1>
            <p className="ml-3 font-light text-base sm:text-lg md:text-xl text-[#1A1F36]/50">
              Manage your availability and bookings.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleTheme}
              className={cn("w-10 h-10 rounded-lg flex items-center justify-center transition-all", isDark ? "bg-white/10 text-white/70 hover:bg-white/20" : "bg-white/60 text-[#1A1F36] hover:bg-white border border-white/60 shadow-sm")}
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </motion.button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Calendar Column */}
          <div className="col-span-1 lg:col-span-8 space-y-8">
            <div className={cn(cardClasses, "p-4 md:p-12")}>
              <div className="flex items-center justify-between mb-12">
                <h2 className="text-4xl font-black tracking-tight">
                  {monthNames[currentMonth]} {currentYear}
                </h2>
                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={handlePrevMonth} className="rounded-full hover:bg-white/10">
                    <ChevronLeft size={24} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={handleNextMonth} className="rounded-full hover:bg-white/10">
                    <ChevronRight size={24} />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center mb-4">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                  <div key={i} className={cn("text-[10px] font-black uppercase tracking-widest opacity-20")}>{d}</div>
                ))}
              </div>

              {isLoading ? (
                <div className="h-[280px] flex flex-col items-center justify-center gap-4">
                  <div className="w-9 h-9 rounded-full border-[3px] border-[#4A6CF7]/20 border-t-[#4A6CF7] animate-spin" />
                  <span className={`text-[10px] font-bold uppercase tracking-[0.25em] ${isDark ? "text-white/30" : "text-[#1A1F36]/30"}`}>Loading calendar</span>
                </div>
              ) : (
              <div className="relative">
                <div className="grid grid-cols-7 gap-y-2 gap-x-0">
                  {calendarGrid.map((cell, idx) => {
                    if (!cell.day) return <div key={`empty-${idx}`} className="h-10" />;

                    const statuses = getDayStatuses(cell.date);
                    const isToday = cell.date === `${new Date().getFullYear()}-${pad2(new Date().getMonth() + 1)}-${pad2(new Date().getDate())}`;
                    const isSelected = selectedDay === cell.date;
                    const isClickStartDay = clickStart === cell.date;

                    // ── Multi-status cell (overlapping ranges) ────────────────────
                    if (statuses.length > 1) {
                      const colors = statuses.map(s => OVERLAP_COLORS[s as keyof typeof OVERLAP_COLORS]);
                      let bgStyle: React.CSSProperties;
                      if (statuses.length === 2) {
                        bgStyle = { background: `linear-gradient(135deg, ${colors[0]} 50%, ${colors[1]} 50%)` };
                      } else {
                        bgStyle = { background: `linear-gradient(to bottom, ${colors[0]} 33.3%, ${colors[1]} 33.3% 66.6%, ${colors[2]} 66.6%)` };
                      }
                      return (
                        <motion.div
                          key={idx}
                          whileHover={{ scale: 1.08, zIndex: 20 }}
                          whileTap={{ scale: 0.97 }}
                          transition={{ type: "spring", stiffness: 400, damping: 25 }}
                          onClick={() => cell.date && handleOccupiedDayClick(cell.date)}
                          className="h-10 flex items-center justify-center cursor-pointer rounded-xl relative overflow-hidden"
                          style={bgStyle}
                        >
                          {isToday ? (
                            <div className="relative h-8 w-8 flex items-center justify-center">
                              <div className="absolute inset-0 rounded-full bg-[#0F172A] shadow-lg shadow-slate-900/40"/>
                              <motion.div className="absolute -inset-1 rounded-full bg-cyan-400/30 blur-md" animate={{scale:[1,1.15,1],opacity:[0.3,0.6,0.3]}} transition={{repeat:Infinity,duration:2,ease:"easeInOut"}}/>
                              <span className="relative z-20 text-[11px] font-extrabold text-white">{cell.day}</span>
                            </div>
                          ) : isSelected ? (
                            <div className="relative h-8 w-8 flex items-center justify-center">
                              <div className="absolute inset-0 rounded-full bg-white shadow-sm"/>
                              <span className="relative z-20 text-[11px] font-extrabold text-[#4A6CF7]">{cell.day}</span>
                            </div>
                          ) : (
                            <span className="text-[11px] font-bold text-white drop-shadow-sm">{cell.day}</span>
                          )}
                        </motion.div>
                      );
                    }

                    // ── Single-status cell (booked / hold / second-hold) ──────────
                    if (statuses.length === 1) {
                      const status = statuses[0] as RangeType;
                      const position = getRunPosition(cell.date, status);
                      const barColor = status === "booked" ? "bg-[#4A6CF7]" : status === "second-hold" ? "bg-[#94A3B8]" : "bg-[#C084FC]";
                      const dayBookingCount = getDayBookings(cell.date).length;
                      let shape = "";
                      if (position === "start") shape = "rounded-l-xl rounded-r-none ml-0.5";
                      else if (position === "middle") shape = "rounded-none";
                      else if (position === "end") shape = "rounded-r-xl rounded-l-none mr-0.5";
                      else shape = "rounded-xl";
                      return (
                        <motion.div
                          key={idx}
                          whileHover={{ scale: 1.08, zIndex: 20 }}
                          whileTap={{ scale: 0.97 }}
                          transition={{ type: "spring", stiffness: 400, damping: 25 }}
                          onClick={() => cell.date && handleOccupiedDayClick(cell.date)}
                          className={`h-10 flex flex-col items-center justify-center cursor-pointer ${barColor} ${shape}`}
                        >
                          {isToday ? (
                            <div className="relative h-8 w-8 flex items-center justify-center">
                              <div className="absolute inset-0 rounded-full bg-[#0F172A] shadow-lg shadow-slate-900/40"/>
                              <motion.div className="absolute -inset-1 rounded-full bg-cyan-400/30 blur-md" animate={{scale:[1,1.15,1],opacity:[0.3,0.6,0.3]}} transition={{repeat:Infinity,duration:2,ease:"easeInOut"}}/>
                              <span className="relative z-20 text-[11px] font-extrabold text-white">{cell.day}</span>
                            </div>
                          ) : isSelected ? (
                            <div className="relative h-8 w-8 flex items-center justify-center">
                              <div className="absolute inset-0 rounded-full bg-white shadow-sm"/>
                              <span className="relative z-20 text-[11px] font-extrabold" style={{ color: status === "booked" ? "#4A6CF7" : status === "second-hold" ? "#94A3B8" : "#C084FC" }}>{cell.day}</span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <span className="text-[11px] font-bold text-white leading-none">{cell.day}</span>
                              {/* Dot indicator for multiple overlapping bookings */}
                              {dayBookingCount > 1 && (
                                <div className="flex gap-0.5 mt-0.5">
                                  {Array.from({ length: Math.min(dayBookingCount - 1, 3) }).map((_, i) => (
                                    <div key={i} className="w-1 h-1 rounded-full bg-white/70" />
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </motion.div>
                      );
                    }

                    // ── Today (no booking) ────────────────────────────────────────
                    if (isToday) {
                      return (
                        <div
                          key={idx}
                          className="h-10 flex items-center justify-center cursor-pointer"
                          onClick={() => cell.date && handleEmptyDayClick(cell.date)}
                        >
                          <div className="relative h-8 w-8 flex items-center justify-center">
                            <div className="absolute inset-0 rounded-full bg-[#0F172A] shadow-lg shadow-slate-900/40"/>
                            <motion.div className="absolute -inset-1 rounded-full bg-cyan-400/30 blur-md" animate={{scale:[1,1.15,1],opacity:[0.3,0.6,0.3]}} transition={{repeat:Infinity,duration:2,ease:"easeInOut"}}/>
                            <span className="relative z-20 text-[11px] font-extrabold text-white">{cell.day}</span>
                          </div>
                        </div>
                      );
                    }

                    // ── Empty day ─────────────────────────────────────────────────
                    return (
                      <div key={idx} className="h-10 flex items-center justify-center">
                        <div
                          onClick={() => cell.date && handleEmptyDayClick(cell.date)}
                          className={cn(
                            "w-full h-full text-[11px] font-bold transition-all flex items-center justify-center rounded-xl border shadow-sm cursor-pointer select-none",
                            isClickStartDay
                              ? "border-[#4A6CF7] bg-[#4A6CF7]/15 text-[#4A6CF7] font-extrabold"
                              : isDark
                                ? "bg-white/5 text-white/40 border-white/5 hover:border-[#4A6CF7]/50 hover:bg-[#4A6CF7]/5"
                                : "bg-white text-[#1A1F36]/60 border-white/50 hover:border-[#4A6CF7]/50 hover:bg-[#4A6CF7]/5"
                          )}
                        >
                          {cell.day}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pending-start hint bar */}
                <AnimatePresence>
                  {clickStart && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.18 }}
                      className={`mt-4 flex items-center gap-3 px-5 py-3 rounded-2xl border shadow-sm ${isDark ? "bg-[#4A6CF7]/10 border-[#4A6CF7]/25" : "bg-[#4A6CF7]/5 border-[#4A6CF7]/20"}`}
                    >
                      <div className="w-2 h-2 rounded-full bg-[#4A6CF7] flex-shrink-0 animate-pulse" />
                      <span className={`text-sm font-bold flex-1 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>
                        Start: {formatDateShort(clickStart, currency, dateFormat)} — now click an end date
                      </span>
                      <button onClick={() => setClickStart(null)} className={`opacity-40 hover:opacity-80 transition-opacity ${isDark ? "text-white" : "text-[#1A1F36]"}`}>
                        <X size={13} />
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Day-detail panel (occupied date) */}
                <AnimatePresence>
                  {selectedDay && !clickStart && (() => {
                    const dayItems = getDayRanges(selectedDay);
                    return (
                      <motion.div
                        key={selectedDay}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        transition={{ duration: 0.18, ease: "easeOut" }}
                        className={`mt-4 rounded-2xl border shadow-sm overflow-hidden ${isDark ? "bg-white/[0.06] border-white/10" : "bg-white border-gray-100 shadow-md"}`}
                      >
                        <div className={`flex items-center justify-between px-5 py-3 border-b ${isDark ? "border-white/10" : "border-gray-100"}`}>
                          <span className={`text-xs font-black uppercase tracking-widest ${isDark ? "text-white/40" : "text-[#1A1F36]/40"}`}>
                            {formatDateShort(selectedDay, currency, dateFormat)}
                          </span>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => {
                                setNewRange(prev => ({ ...prev, start: selectedDay, end: '' }));
                                setShowAddModal(true);
                              }}
                              className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#4A6CF7] hover:opacity-70 transition-opacity"
                            >
                              <Plus size={11} />
                              Add Range
                            </button>
                            <button onClick={() => setSelectedDay(null)} className={`opacity-40 hover:opacity-80 transition-opacity ${isDark ? "text-white" : "text-[#1A1F36]"}`}>
                              <X size={13} />
                            </button>
                          </div>
                        </div>
                        {dayItems.length === 0 ? (
                          <div className="px-5 py-4">
                            <span className={`text-xs font-bold ${isDark ? "text-white/30" : "text-[#1A1F36]/30"}`}>No ranges for this date.</span>
                          </div>
                        ) : (
                          dayItems.map((item, i) => (
                            <div
                              key={item.id}
                              className={`flex items-center gap-3 px-5 py-3 ${i < dayItems.length - 1 ? `border-b ${isDark ? "border-white/5" : "border-gray-50"}` : ""}`}
                            >
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${item.type === "booked" ? "bg-[#4A6CF7]" : item.type === "second-hold" ? "bg-[#94A3B8]" : "bg-[#C084FC]"}`} />
                              <span className={`text-sm font-bold flex-1 truncate ${isDark ? "text-white" : "text-[#1A1F36]"}`}>{item.project || "Unnamed"}</span>
                              <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md flex-shrink-0 ${
                                item.type === "booked" ? "bg-[#4A6CF7]/10 text-[#4A6CF7]"
                                : item.type === "second-hold" ? "bg-[#94A3B8]/10 text-[#94A3B8]"
                                : "bg-[#C084FC]/10 text-[#C084FC]"
                              }`}>
                                {item.type === "booked" ? "Booked" : item.type === "second-hold" ? "2nd Hold" : "1st Hold"}
                              </span>
                              <button
                                onClick={() => openAgendaEdit(item)}
                                className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:text-[#4A6CF7] flex-shrink-0 ${isDark ? "text-white/20 hover:bg-white/5" : "text-[#1A1F36]/20 hover:bg-gray-50"}`}
                              >
                                <Pencil size={12} />
                              </button>
                            </div>
                          ))
                        )}
                      </motion.div>
                    );
                  })()}
                </AnimatePresence>
              </div>
              )}

              {/* Legend */}
              {!isLoading && (
                <div className={`flex items-center gap-5 mt-6 flex-wrap ${isDark ? "text-white/25" : "text-[#1A1F36]/30"}`}>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: `linear-gradient(135deg, ${OVERLAP_COLORS.booked} 50%, ${OVERLAP_COLORS.hold} 50%)` }} />
                    <span className="text-[10px] font-bold uppercase tracking-widest">Split cell = overlap</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm flex-shrink-0 bg-[#4A6CF7] flex items-center justify-center">
                      <div className="w-1 h-1 rounded-full bg-white/70" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-widest">Dot = multiple bookings</span>
                  </div>
                </div>
              )}

              {!isLoading && ranges.length === 0 && bookings.length === 0 && (
                <p className={`text-center mt-5 text-[10px] font-bold uppercase tracking-[0.2em] ${isDark ? "text-white/20" : "text-[#1A1F36]/25"}`}>
                  No bookings or holds added yet. Click a date to get started.
                </p>
              )}

              {!isLoading && earliestAvailable && (
                <div className={`mt-5 flex items-center gap-4 px-5 py-4 rounded-2xl ${isDark ? "bg-white/5 border border-white/10" : "bg-[#F0F4FF] border border-[#4A6CF7]/12"}`}>
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isDark ? "bg-emerald-400" : "bg-emerald-500"}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-[9px] font-black uppercase tracking-[0.2em] mb-0.5 ${isDark ? "text-white/30" : "text-[#1A1F36]/40"}`}>
                      Next Available Window
                    </p>
                    <p className={`text-sm font-black ${isDark ? "text-white" : "text-[#1A1F36]"}`}>
                      {formatDateShort(`${earliestAvailable.start.getFullYear()}-${pad2(earliestAvailable.start.getMonth() + 1)}-${pad2(earliestAvailable.start.getDate())}`, currency, dateFormat)}
                      {earliestAvailable.end
                        ? ` — ${formatDateShort(`${earliestAvailable.end.getFullYear()}-${pad2(earliestAvailable.end.getMonth() + 1)}-${pad2(earliestAvailable.end.getDate())}`, currency, dateFormat)}`
                        : ' onwards'}
                    </p>
                  </div>
                  {earliestAvailable.days !== null && (
                    <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full flex-shrink-0 ${isDark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-500/10 text-emerald-600"}`}>
                      {earliestAvailable.days} days
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Agenda/Timeline */}
            <div className={cn(cardClasses, "p-4 md:p-12")}>
              <h3 className={cn(labelClasses, "mb-10")}>Monthly Agenda</h3>
              <div className="relative">
                <div className="divide-y overflow-y-auto overflow-x-hidden px-2 [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-black/20 [&::-webkit-scrollbar-track]:bg-transparent"
                  style={{ maxHeight: "320px", scrollbarColor: isDark ? "rgba(255,255,255,0.15) transparent" : "rgba(0,0,0,0.15) transparent" }}>
                {isLoading ? (
                  <p className="text-center py-12 opacity-30 font-bold uppercase text-[10px] tracking-widest">Loading…</p>
                ) : filteredItems.length === 0 ? (
                  <p className="text-center py-12 opacity-30 font-bold uppercase text-[10px] tracking-widest">No events scheduled</p>
                ) : (
                  filteredItems.map((item: AgendaItem) => (
                    <div key={item.id} className={`flex items-center gap-6 py-5 border-b ${isDark ? "border-white/5" : "border-gray-100"}`}>
                      <div className={cn(
                        "w-3 h-3 rounded-full shrink-0",
                        item.type === "booked" ? "bg-[#4A6CF7]" : item.type === "second-hold" ? "bg-[#94A3B8]" : "bg-[#C084FC]"
                      )} />
                      <div className="flex-1 min-w-0">
                        <p className={cn("font-bold text-sm mb-0.5 truncate", isDark ? "text-white" : "text-[#1A1F36]")}>{item.project || "Unnamed Project"}</p>
                        <p className={`text-[10px] font-bold uppercase tracking-tighter ${isDark ? "text-white/40" : "text-[#1A1F36]/40"}`}>{formatDateShort(item.start,currency,dateFormat)} — {formatDateShort(item.end,currency,dateFormat)}</p>
                      </div>
                      <div className={cn(
                        "px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest shrink-0",
                        item.type === "booked" ? "bg-[#4A6CF7]/10 text-[#4A6CF7]" : item.type === "second-hold" ? "bg-[#94A3B8]/10 text-[#94A3B8]" : "bg-[#C084FC]/10 text-[#C084FC]"
                      )}>
                        {item.type === "booked" ? "Booked" : item.type === "second-hold" ? sh : fh}
                      </div>
                      <button
                        onClick={() => openAgendaEdit(item)}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:text-[#4A6CF7] hover:scale-110 active:scale-95 shrink-0 ${isDark ? "text-white/30 hover:bg-white/5" : "text-[#1A1F36]/30 hover:bg-gray-100"}`}
                      >
                        <Pencil size={14} />
                      </button>
                    </div>
                  ))
                )}
                </div>
                {filteredItems.length > 4 && (
                  <div className={`pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t to-transparent ${isDark ? "from-[#0f1419]" : "from-white/80"}`} />
                )}
              </div>
            </div>
          </div>

          {/* Right Controls Column */}
          <div className="col-span-1 lg:col-span-4 space-y-8">
            {/* Add Range Card */}
            <div className={cn(cardClasses, "p-5 md:p-8 space-y-8")}>
              <div className="flex items-center gap-3">
                <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", isDark ? "bg-white/5 text-white" : "bg-black text-white")}>
                  <Plus size={20} />
                </div>
                <h3 className={labelClasses}>Add Range</h3>
              </div>
              {addRangeForm}
            </div>

            {/* Sync Services card hidden — Google Calendar sync not yet implemented */}
          </div>
        </div>
      </main>

      {/* Add Range Modal (opened from two-click calendar flow or day-detail "Add Range") */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-8 backdrop-blur-xl bg-black/40 overflow-y-auto [@supports_not_(backdrop-filter:blur(1px))]:bg-black/80"
            onClick={(e) => { if (e.target === e.currentTarget) setShowAddModal(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              className={`relative w-full max-w-md rounded-[2.5rem] border shadow-xl p-5 md:p-10 ${isDark ? "bg-[#1a1f2e] border-white/10" : "bg-white border-gray-200"}`}
            >
              <button
                onClick={() => setShowAddModal(false)}
                className={`absolute top-6 right-6 w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:scale-110 active:scale-95 ${isDark ? "text-white/30 hover:text-white hover:bg-white/10" : "text-[#1A1F36]/30 hover:text-[#1A1F36] hover:bg-black/5"}`}
              >
                <X size={20} />
              </button>
              <h2 className={`text-2xl font-black mb-8 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Add Range</h2>
              {addRangeForm}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Google Connect Modal Mockup */}
      <AnimatePresence>
        {showGoogleModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowGoogleModal(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm [@supports_not_(backdrop-filter:blur(1px))]:bg-black/90"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={cn(
                "relative w-full max-w-lg p-5 md:p-12 rounded-[3rem] border shadow-2xl",
                isDark ? "bg-[#131820] border-white/10 text-white" : "bg-white border-gray-200 text-[#1A1F36]"
              )}
            >
              <div className="flex flex-col items-center text-center space-y-6">
                <div className="w-20 h-20 rounded-3xl bg-blue-500/10 flex items-center justify-center mb-4">
                  <CalendarIcon size={40} className="text-blue-500" />
                </div>
                <h2 className="text-3xl font-black tracking-tight">Connect Google Calendar</h2>
                <p className="opacity-50 font-medium">
                  Sync your bookings and holds automatically with your Google Calendar.
                </p>
                <div className="flex items-center gap-2 p-4 rounded-2xl bg-amber-500/10 text-amber-500 border border-amber-500/20 text-xs font-bold uppercase tracking-widest">
                  <AlertCircle size={16} />
                  <span>MOCKUP: No real data will be synced.</span>
                </div>
                <div className="flex w-full gap-4 pt-4">
                  <Button
                    variant="ghost"
                    onClick={() => setShowGoogleModal(false)}
                    className="flex-1 h-14 rounded-full font-black uppercase tracking-widest transition-all"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      setIsGoogleConnected(true);
                      setShowGoogleModal(false);
                      toast({ title: "Connected", description: "Google Calendar sync is active (Mock)." });
                    }}
                    className="flex-1 h-14 rounded-full bg-black hover:bg-black/90 text-white font-black uppercase tracking-widest shadow-xl shadow-black/20"
                  >
                    Connect Now
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Agenda Item Edit Modal */}
      <AnimatePresence>
        {editingAgendaItem && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-8 backdrop-blur-xl bg-black/40 overflow-y-auto [@supports_not_(backdrop-filter:blur(1px))]:bg-black/80"
            onClick={(e) => { if (e.target === e.currentTarget) setEditingAgendaItem(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              className={`relative w-full max-w-md rounded-[2.5rem] border shadow-xl p-5 md:p-10 ${isDark ? "bg-[#1a1f2e] border-white/10" : "bg-white border-gray-200"}`}
            >
              <button
                onClick={() => setEditingAgendaItem(null)}
                className={`absolute top-6 right-6 w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:scale-110 active:scale-95 ${isDark ? "text-white/30 hover:text-white hover:bg-white/10" : "text-[#1A1F36]/30 hover:text-[#1A1F36] hover:bg-black/5"}`}
              >
                <X size={20} />
              </button>

              <h2 className={`text-2xl font-black mb-6 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>
                {editingAgendaItem.source === 'booking' ? (agendaDraft.project || "Edit Booking") : "Edit Hold / Range"}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className={`block text-[10px] font-bold uppercase tracking-[0.2em] mb-2 ${isDark ? "text-white/40" : "text-[#1A1F36]/40"}`}>
                    {editingAgendaItem.source === 'booking' ? "Company" : "Project / Label"}
                  </label>
                  <input
                    className={`w-full px-4 py-3 rounded-2xl border transition-all outline-none ${isDark ? "bg-white/5 border-white/10 text-white placeholder-white/20 focus:border-white/30" : "bg-white/50 border-gray-200 text-[#1A1F36] placeholder-[#1A1F36]/30 focus:border-gray-400"}`}
                    value={agendaDraft.project}
                    onChange={e => setAgendaDraft(d => ({ ...d, project: e.target.value }))}
                    placeholder={editingAgendaItem.source === 'booking' ? "Company name" : "Optional label"}
                  />
                </div>

                <div>
                    <label className={`block text-[10px] font-bold uppercase tracking-[0.2em] mb-2 ${isDark ? "text-white/40" : "text-[#1A1F36]/40"}`}>Type</label>
                    <div className={`flex p-1 rounded-2xl border ${isDark ? "bg-white/5 border-white/10" : "bg-gray-100 border-gray-200"}`}>
                      {(["booked", "hold", "second-hold"] as RangeType[]).map(t => (
                        <button key={t} onClick={() => setAgendaDraft(d => ({ ...d, type: t }))}
                          className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all capitalize ${agendaDraft.type === t ? (isDark ? "bg-white/10 text-white" : "bg-white text-black shadow-sm") : "text-gray-400"}`}>
                          {t === "booked" ? "Booked" : t === "hold" ? fh : sh}
                        </button>
                      ))}
                    </div>
                  </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-[10px] font-bold uppercase tracking-[0.2em] mb-2 ${isDark ? "text-white/40" : "text-[#1A1F36]/40"}`}>Start Date</label>
                    <input type="date"
                      className={`w-full px-4 py-3 rounded-2xl border transition-all outline-none ${isDark ? "bg-white/5 border-white/10 text-white focus:border-white/30" : "bg-white/50 border-gray-200 text-[#1A1F36] focus:border-gray-400"}`}
                      value={agendaDraft.start}
                      onChange={e => setAgendaDraft(d => ({ ...d, start: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className={`block text-[10px] font-bold uppercase tracking-[0.2em] mb-2 ${isDark ? "text-white/40" : "text-[#1A1F36]/40"}`}>End Date</label>
                    <input type="date"
                      className={`w-full px-4 py-3 rounded-2xl border transition-all outline-none ${isDark ? "bg-white/5 border-white/10 text-white focus:border-white/30" : "bg-white/50 border-gray-200 text-[#1A1F36] focus:border-gray-400"}`}
                      value={agendaDraft.end}
                      min={agendaDraft.start || undefined}
                      onFocus={e => {
                        if (!e.target.value && agendaDraft.start) {
                          e.target.value = agendaDraft.start;
                          setTimeout(() => { if (!agendaDraft.end) e.target.value = ''; }, 0);
                        }
                      }}
                      onChange={e => setAgendaDraft(d => ({ ...d, end: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  onClick={handleSaveAgendaItem}
                  className="flex-1 bg-black text-white py-3 rounded-full font-bold uppercase tracking-[0.2em] hover:opacity-80 transition-opacity active:scale-95"
                >
                  Save
                </button>
                <button
                  onClick={async () => {
                    const item = editingAgendaItem;
                    setEditingAgendaItem(null);
                    await handleDeleteItem(item);
                  }}
                  className="flex-1 border border-red-200 text-red-400 py-3 rounded-full font-bold uppercase tracking-[0.2em] hover:bg-red-50 hover:opacity-80 active:scale-95 transition-all"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
